'use client';
import { useState, useLayoutEffect, type RefObject } from 'react';
import { uiZoom } from './uiZoom';

/** What lies between an element's top and the bottom of the window, in CSS px. `space` is the visible
 *  height of the scroll container it sits in (the shell's `<main>`, whose bottom is the window's) less the
 *  element's own top offset inside it, less every bottom padding between the two; it is negative when the
 *  element already starts below the fold. `gutter` is that summed
 *  bottom padding: the room the page keeps under its last element. `marginTop` is the element's own top
 *  margin, so a caller can tell the room the content above leaves from the room its spacing takes. */
interface FillMeasure { space: number; gutter: number; marginTop: number }

/** See {@link FillMeasure}.
 *
 *  Those gutters are READ from the layout rather than restated as a constant here. The page's padding
 *  stays defined in exactly one place — its CSS — and changing it there cannot silently push the element
 *  back past the fold and bring the page scrollbar with it.
 *
 *  Geometry under the shell's root zoom comes back in visual px, not CSS px (see uiZoom), so the measured
 *  terms are divided by it; computed paddings are already CSS px and are not. Undefined until measured,
 *  so the server renders whatever fallback the stylesheet gives. */
export function useFillMeasure(ref: RefObject<HTMLElement | null>): FillMeasure | undefined {
  const [measured, setMeasured] = useState<FillMeasure | undefined>(undefined);

  // A layout effect, so the first measurement lands before the browser paints: a caller that lays out
  // from it (the instance wordmark) is never drawn in its unmeasured state.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const scroller = el.closest('main') ?? document.documentElement;

    const measure = () => {
      const zoom = uiZoom();
      let gutter = parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
      for (let node = el.parentElement; node && node !== scroller; node = node.parentElement) {
        gutter += parseFloat(getComputedStyle(node).paddingBottom) || 0;
      }
      // A bounding rect is viewport-relative. Once the page has scrolled, using it directly makes the
      // measured top negative and inflates the fill height by the current scroll offset. Phone browsers
      // resize the visual viewport as their chrome hides/shows, so one resize near the bottom could freeze
      // thousands of pixels of empty flex space under the transcript. Convert the visual position back to
      // the element's stable position inside the scroller before deriving the remaining viewport height.
      const top = (el.getBoundingClientRect().top - scroller.getBoundingClientRect().top) / zoom + scroller.scrollTop;
      // The floor is the bottom of the scroller's visible box, in the same scroller-relative CSS px as
      // `top`. <main> does not start at the top of the window: the shell's top bar sits above it, and
      // counting from the window's height instead overshot by exactly that bar, so every filled page and
      // every page closed by the floor wordmark scrolled by the bar's height. `clientHeight` is already
      // CSS px under the root zoom; the viewport of the document fallback is not.
      const visible = scroller === document.documentElement ? window.innerHeight / zoom : scroller.clientHeight;
      const space = Math.round(visible - top - gutter);
      const next = { space, gutter: Math.round(gutter), marginTop: Math.round(parseFloat(getComputedStyle(el).marginTop) || 0) };
      setMeasured((prev) => (prev && prev.space === next.space && prev.gutter === next.gutter && prev.marginTop === next.marginTop ? prev : next));
    };

    // Re-measure on anything that can move the element: the window resizing, the advisor dock opening,
    // a header wrapping onto a second line, a disclosure panel opening above it. The scroll container
    // catches the first ones. What grows ABOVE the element is watched directly — every element before
    // it, at each level up to the scroll container — because a caller that sizes the element from this
    // measurement keeps the page's total height constant, so no container around it ever resizes. The
    // element's own height never feeds the measurement (only its top does), so its resize settles on the
    // same value instead of looping. Elements mounted or removed above it re-collect the watched set.
    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; measure(); });
    };
    const resizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    const watchAbove = () => {
      if (!resizes) return;
      resizes.disconnect();
      resizes.observe(scroller);
      for (let node: Element | null = el; node && node !== scroller; node = node.parentElement) {
        for (let sibling = node.previousElementSibling; sibling; sibling = sibling.previousElementSibling) resizes.observe(sibling);
      }
    };
    const mutations = typeof MutationObserver === 'undefined' ? null : new MutationObserver(() => { watchAbove(); schedule(); });
    measure();
    watchAbove();
    mutations?.observe(scroller, { childList: true, subtree: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('resize', schedule);
      resizes?.disconnect();
      mutations?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref]);

  return measured;
}

/** The height that makes an element reach the bottom of the window, never below `minPx`. */
export function useFillHeight(ref: RefObject<HTMLElement | null>, minPx = 320): number | undefined {
  const measured = useFillMeasure(ref);
  return measured === undefined ? undefined : Math.max(minPx, measured.space);
}
